// @flow
import React, { useContext } from 'react';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { Grid } from 'semantic-ui-react';
import { StyledH5 } from '@presentational/headings/StyledH5';
import { IS_FREIGHT_CONNECTOR_LIVE } from '@streaming-projects/constants';

import {
  CLUSTER_AVAILABLE_CONNECTORS_LIST_BACKEND_NAME,
  CLUSTER_CONNECTORS_FORM,
  CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_PROVIDER_BACKEND_NAME,
  CLUSTER_REGION_BACKEND_NAME,
  CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME,
  CONNECTOR_DESTINATION_REGION_BACKEND_NAME,
  CONNECTOR_NAME,
  DEFAULT_VALUE_FOR_CONNECTOR_NETWORKING_TYPE,
  DEFAULT_VALUE_FOR_CONNECTOR_SMT_TYPE,
  DEFAULT_VALUE_FOR_DB_ROW_ID_STR,
  FREIGHT_CLUSTER_TYPE,
  IS_CONNECT_SMT_TYPE_LIVE,
} from '../../../constants';
import { getMonthName } from '../cluster-input-table/utils';
import { PrimaryButton } from '../../presentational/buttons/PrimaryButton';
import { SelectField } from '../../../formik-utils/FormFields';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { getDropdownOptions } from '../../stream-governance/utils';
import {
  CLUSTER_DETAILS_CONFIG_MAP,
  CLUSTER_DETAILS_FORM_NAMES_TO_FIELDS_MAP,
} from '../../../configuration/cluster-details';
import { EstimateContext } from '../../../contexts/EstimateContext';
import { ClusterContext } from '../../../contexts/ClusterContext';
import { isAnythingEditedOnClusterPageBesidesTheGivenForm } from '../../views/utils';
import { isFormEdited } from '../cluster-details-inputs/utils';

import { ConnectorsMonthlyInputContainerWrapper } from './ConnectorsMonthlyInputContainerWrapper';
import { getDestinationDropdownsVisibility } from './utils';

export const connectorsRowConfig = [
  {
    backendName: 'is_enabled',
    displayName: 'Active',
    isBooleanField: true,
    defaultValue: true,
  },
  {
    backendName: 'task_count',
    displayName: 'Number of Tasks',
    defaultValue: 0,
  },
  {
    backendName: 'throughput_average_mbps',
    displayName: 'Average Throughput (MBps)',
    defaultValue: 0,
  },
  {
    backendName: 'num_cores',
    displayName: 'Number of Cores',
    defaultValue: 0,
    showOrHideFunc: (values, contextData) => {
      return contextData?.connectorName === CONNECTOR_NAME.ORACLE_XSTREAM_SOURCE;
    },
  },
];

const getConnectorWithDefaults = (values, connectorNameToRecordsMap, totalNumberOfMonths) => {
  const connectorName = getIn(values, CLUSTER_AVAILABLE_CONNECTORS_LIST_BACKEND_NAME);
  const kafkaMonthlyInputs = getIn(values, [CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG, 'months']);
  const clusterProvider = getIn(values, CLUSTER_PROVIDER_BACKEND_NAME);
  const clusterRegion = getIn(values, CLUSTER_REGION_BACKEND_NAME);

  // When shown in the UI, Destination Provider and Region values should default to the cluster's provider and region value.
  const { showDestinationProvider, showDestinationRegion } = getDestinationDropdownsVisibility({
    connectorName,
    networkingType: DEFAULT_VALUE_FOR_CONNECTOR_NETWORKING_TYPE,
    destinationProvider: clusterProvider,
    clusterProvider,
  });

  const toRet = {
    connectorName: connectorName,
    connectorNetworkingType: DEFAULT_VALUE_FOR_CONNECTOR_NETWORKING_TYPE,
    ...(IS_CONNECT_SMT_TYPE_LIVE && {
      connectorSmtType: DEFAULT_VALUE_FOR_CONNECTOR_SMT_TYPE,
    }),
    ...(showDestinationProvider && {
      [CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME]: clusterProvider,
    }),
    ...(showDestinationRegion && {
      [CONNECTOR_DESTINATION_REGION_BACKEND_NAME]: clusterRegion,
    }),
    connectorLabel: connectorNameToRecordsMap.get(connectorName)[0].connector_label,
    connectorId: DEFAULT_VALUE_FOR_DB_ROW_ID_STR,
  };

  const months = {};

  for (let month = 1; month <= totalNumberOfMonths; month++) {
    const monthName = getMonthName(month);
    const currMonthObj = { name: monthName, month: month };
    for (const row of connectorsRowConfig) {
      currMonthObj[row.backendName] = row.defaultValue;
      currMonthObj.is_enabled = kafkaMonthlyInputs[monthName].is_enabled;
    }
    months[monthName] = currMonthObj;
  }
  toRet.months = months;
  return toRet;
};

export const AddKafkaConnectorComponent = ({
  disabled,
  keyNameInValues,
  connectorOptionsConfig,
  connectorOptions,
  values,
  initialValues,
  connectorNameToRecordsMap,
  numberOfMonths,
  MonthlyInputContainerWrapper = ConnectorsMonthlyInputContainerWrapper,
}) => (
  <PaddedAndRaisedSegment disabled={disabled}>
    <FieldArray
      name={`${keyNameInValues}.connectors`}
      render={(arrayHelpers) => {
        return (
          <>
            <Grid>
              <Grid.Row columns={5}>
                <Grid.Column verticalAlign="bottom">
                  <SelectField
                    disabled={disabled}
                    fieldDisplayName={connectorOptionsConfig.displayName}
                    fieldName={connectorOptionsConfig.backendFieldName}
                    options={connectorOptions}
                    search={true}
                  />
                </Grid.Column>
                <Grid.Column verticalAlign="bottom">
                  <PrimaryButton
                    disabled={
                      !getIn(values, CLUSTER_AVAILABLE_CONNECTORS_LIST_BACKEND_NAME) ||
                      disabled ||
                      isFormEdited(
                        values,
                        initialValues,
                        CLUSTER_CONNECTORS_FORM,
                        CLUSTER_DETAILS_FORM_NAMES_TO_FIELDS_MAP
                      )
                    }
                    onClick={() =>
                      arrayHelpers.push(
                        getConnectorWithDefaults(values, connectorNameToRecordsMap, numberOfMonths)
                      )
                    }
                    size="small"
                    text="Add Connector"
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <MonthlyInputContainerWrapper arrayHelpers={arrayHelpers} disabled={disabled} />
          </>
        );
      }}
    />
  </PaddedAndRaisedSegment>
);

export const ConnectorsTopLevelContainer = () => {
  const { values, initialValues } = useFormikContext();

  const cluster = useContext(ClusterContext);
  const estimate = useContext(EstimateContext);

  const isFreightCluster = cluster?.inputs?.cluster_type === FREIGHT_CLUSTER_TYPE;

  if (isFreightCluster && !IS_FREIGHT_CONNECTOR_LIVE) {
    return (
      <PaddedAndRaisedSegment>
        <StyledH5 text="Freight does not support connectors!" />
      </PaddedAndRaisedSegment>
    );
  }

  const connectorsList = cluster?.cluster_configs?.full_connectors_list;

  const numberOfMonths = estimate.deal_duration.deal_duration_months;
  const keyNameInValues = CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG;

  const connectorOptionsConfig = CLUSTER_DETAILS_CONFIG_MAP.get(
    CLUSTER_AVAILABLE_CONNECTORS_LIST_BACKEND_NAME
  );

  const fullConnectorsList = connectorsList.connector_types;
  const connectorNameToRecordsMap = cluster?.cluster_configs?.connectorNameToRecordsMap;

  const connectorOptions = getDropdownOptions(
    fullConnectorsList,
    values,
    connectorOptionsConfig,
    false,
    false,
    false,
    true
  );

  const disabled = isAnythingEditedOnClusterPageBesidesTheGivenForm(
    values,
    initialValues,
    CLUSTER_CONNECTORS_FORM
  );

  return (
    <AddKafkaConnectorComponent
      connectorNameToRecordsMap={connectorNameToRecordsMap}
      connectorOptions={connectorOptions}
      connectorOptionsConfig={connectorOptionsConfig}
      disabled={disabled}
      initialValues={initialValues}
      keyNameInValues={keyNameInValues}
      numberOfMonths={numberOfMonths}
      values={values}
    />
  );
};

// todo:: fix the issue with Accordions index when adding and deleting connectors.
// Replicate the existing behaviour
